import clsx from 'clsx'
import BaseManagementComponent from 'components/BaseManagementComponent'
import FilterSelect from 'components/InfiniteSelect/FilterSelect'
import InputSearchKeyword from 'components/InputSearchKeyword'
import { useStore } from 'context/store'
import dayjs from 'dayjs'
import { observer } from 'mobx-react'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { InputText } from 'primereact/inputtext'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { DATE_TIME_FORMAT } from 'utils/constants/datetime'
import { EventStatusEnum } from 'utils/constants/event'
import { useIsMobile } from 'utils/hooks/useIsMobile'
import { useObjectSearchParams } from 'utils/hooks/useObjectSearchParams'
import { getEventStatusTag } from 'utils/helper/table'

const EventManagement = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const { searchObject, setRestSearchObject } = useObjectSearchParams()
  const [showMobileFilter, setShowMobileFilter] = useState(false)
  const [mobileKeyword, setMobileKeyword] = useState('')
  const [mobileStatus, setMobileStatus] = useState<string | null>(null)

  useEffect(() => {
    setMobileKeyword((searchObject?.keyword as string) || '')
    setMobileStatus((searchObject?.status as string) || null)
  }, [searchObject?.keyword, searchObject?.status])

  const eventStatusOptions = useMemo(
    () =>
      Object.values(EventStatusEnum).map((e) => ({
        label: t(e),
        value: e,
      })),
    [t]
  )

  const {
    eventManamentStore: {
      loadingListing,
      handleFilterDataChange,
      listData: { data, meta },
    },
  } = useStore()

  return (
    <BaseManagementComponent
      dataSource={data}
      loading={loadingListing}
      actionColumns={[
        {
          key: 'go_to_registraion',
          icon: <i className="isax-arrow-right-2"></i>,
          href: (item) => item.link,
          linkTarget: '_blank',
          tooltip: t('Go to booking site'),
        },
        {
          key: 'view',
          icon: <i className="isax-eye"></i>,
          href: (item) => `/event-management/${item._id}`,
          tooltip: t('View'),
        },
        {
          key: 'registration',
          icon: <i className="isax-people"></i>,
          href: (item) => `/event-management/${item._id}/registration`,
          tooltip: t('Registration list'),
        },
        {
          key: 'clone',
          icon: <i className="isax-copy"></i>,
          href: (item) => `/event-management/create?cloneId=${item._id}`,
          tooltip: t('Clone Event'),
        },
        // {
        //   key: 'delete',
        //   icon: <i className="isax-trash"></i>,
        //   showConfirm: {},
        //   action: (data) => {
        //     deleteItem(data._id)
        //   },
        //   tooltip: t('Delete'),
        // },
      ]}
      columns={[
        {
          key: 'title',
          dataIndex: 'title',
          header: t('Event name'),
        },
        {
          key: 'location',
          header: t('Location'),
          width: '20%',
        },

        {
          key: 'startTimeEvent',
          sortable: true,
          body: (data) =>
            dayjs(data.startTimeEvent).format(DATE_TIME_FORMAT.FULL),
          width: 200,

          header: t('Start Time'),
        },
        {
          key: 'maxRegistrations',
          header: t('Registration'),
          body: (data) => (
            <div
              className={clsx(
                data.registeredQuantityEvent === data.maxRegistrationsEvent
                  ? 'text-red-800'
                  : ''
              )}
            >
              {(data.registeredQuantityEvent ?? 0) +
                '/' +
                data.maxRegistrationsEvent}
            </div>
          ),
          width: 120,
        },
        {
          key: 'status',
          filter: true,

          filterElement: (options) => {
            return (
              <FilterSelect
                options={Object.values(EventStatusEnum).map((e) => ({
                  label: t(e),
                  value: e,
                }))}
                showSearch={false}
                className="w-20rem"
                multiple={false}
                value={options.value}
                onSelectItem={(_, value) => {
                  options.filterCallback(value, options.index)
                }}
                onChange={(e) => options.filterApplyCallback(e, options.index)}
                placeholder={t('Select One')}
              />
            )
          },
          showFilterMatchModes: false,
          filterField: 'status',
          filterMatchMode: 'contains',
          body: (item) => getEventStatusTag(item.status),
          header: t('Status'),
          width: 120,
        },
      ]}
      pagination={{
        ...meta,
      }}
      handleFilterDataChange={handleFilterDataChange}
      mobileListRender={(item) => (
        <div
          className="border-1 surface-border border-round-lg p-3 cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/event-management/${item._id}`)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              navigate(`/event-management/${item._id}`)
            }
          }}
        >
          <div className="flex justify-content-between align-items-start gap-2 mb-2">
            <div className="font-semibold text-lg line-height-3">{item.title}</div>
            {getEventStatusTag(item.status)}
          </div>
          <div className="text-sm text-color-secondary mb-2">
            <i className="isax-location mr-2" />
            {item.location || '-'}
          </div>
          <div className="text-sm mb-1">
            <span className="font-medium mr-2">{t('Start Time')}:</span>
            {dayjs(item.startTimeEvent).format(DATE_TIME_FORMAT.FULL)}
          </div>
          <div
            className={clsx(
              'text-sm mb-3',
              item.registeredQuantityEvent === item.maxRegistrationsEvent
                ? 'text-red-800'
                : ''
            )}
          >
            <span className="font-medium mr-2">{t('Registration')}:</span>
            {(item.registeredQuantityEvent ?? 0) + '/' + item.maxRegistrationsEvent}
          </div>
          <div className="flex gap-3" onClick={(e) => e.stopPropagation()}>
            <Link
              to={item.link}
              target="_blank"
              className="flex justify-content-center align-items-center text-gray-500 no-underline"
            >
              <i className="isax-arrow-right-2"></i>
            </Link>
            <Link
              to={`/event-management/${item._id}`}
              className="flex justify-content-center align-items-center text-gray-500 no-underline"
            >
              <i className="isax-eye"></i>
            </Link>
            <Link
              to={`/event-management/${item._id}/registration`}
              className="flex justify-content-center align-items-center text-gray-500 no-underline"
            >
              <i className="isax-people"></i>
            </Link>
            <Link
              to={`/event-management/create?cloneId=${item._id}`}
              className="flex justify-content-center align-items-center text-gray-500 no-underline"
            >
              <i className="isax-copy"></i>
            </Link>
          </div>
        </div>
      )}
      filterComponent={
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-3">
          <h1 className="text-2xl md:text-3xl m-0">{t('Event List')}</h1>
          <div className="flex flex-column md:flex-row gap-3 md:gap-4 w-full md:w-auto">
            {!isMobile ? (
              <div className="w-full md:w-20rem">
                <InputSearchKeyword placeholder={t('Search for Title, Location')} />
              </div>
            ) : (
              <Button
                type="button"
                icon="isax isax-filter-search"
                label={t('Filter')}
                className="w-full"
                severity="secondary"
                onClick={() => setShowMobileFilter(true)}
              />
            )}
            <Link to={'create'} className="w-full md:w-auto">
              <Button
                icon="isax isax-add"
                label={t('Add new', { item: t('Event') })}
                className="w-full md:w-auto"
              >
                &nbsp;
              </Button>
            </Link>
          </div>
          <Dialog
            visible={showMobileFilter}
            onHide={() => setShowMobileFilter(false)}
            showHeader={false}
            closable={false}
            draggable={false}
            resizable={false}
            modal
            className="mobile-select-branch-dialog m-0 shadow-none border-noround"
            contentClassName="p-0 h-full"
            style={{
              width: '100vw',
              maxWidth: '100vw',
              height: '100vh',
              maxHeight: '100vh',
            }}
          >
            <div className="w-full h-full bg-gray-100 p-3 relative">
              <div className="flex align-items-center justify-content-between mb-3">
                <h2 className="m-0 text-xl">{t('Filter')}</h2>
                <button
                  type="button"
                  className="mobile-drawer-close-btn"
                  onClick={() => setShowMobileFilter(false)}
                  aria-label={t('Close')}
                >
                  <i className="isax isax-close-01" />
                </button>
              </div>
              <div className="card m-0 p-3 flex flex-column gap-3">
                <div>
                  <label className="font-semibold mb-2 block">{t('Keyword')}</label>
                  <InputText
                    value={mobileKeyword}
                    onChange={(e) => setMobileKeyword(e.target.value)}
                    placeholder={t('Search for Title, Location')}
                    className="w-full"
                    maxLength={50}
                  />
                </div>
                <div>
                  <label className="font-semibold mb-2 block">{t('Status')}</label>
                  <FilterSelect
                    options={eventStatusOptions}
                    showSearch={false}
                    className="w-full"
                    multiple={false}
                    value={mobileStatus ? [mobileStatus] : []}
                    onSelectItem={(_, selectedValues) => {
                      setMobileStatus((selectedValues?.[0] as string) || null)
                    }}
                    showFooter={false}
                    placeholder={t('Select One')}
                  />
                </div>
              </div>
              <div className="fixed left-0 right-0 bottom-0 p-3 bg-white border-top-1 surface-border flex gap-3">
                <Button
                  type="button"
                  outlined
                  label={t('Clear')}
                  className="flex-1"
                  onClick={() => {
                    setMobileKeyword('')
                    setMobileStatus(null)
                    setRestSearchObject({
                      keyword: null,
                      status: null,
                      page: 1,
                    })
                    setShowMobileFilter(false)
                  }}
                />
                <Button
                  type="button"
                  label={t('Apply')}
                  className="flex-1"
                  onClick={() => {
                    setRestSearchObject({
                      keyword: mobileKeyword.trim() || null,
                      status: mobileStatus || null,
                      page: 1,
                    })
                    setShowMobileFilter(false)
                  }}
                />
              </div>
            </div>
          </Dialog>
        </div>
      }
    />
  )
}
export default observer(EventManagement)
